/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorInfo } from 'react';
import { differenceInSeconds } from 'date-fns';

import { env } from 'config/env';

import { getSavedUserInfo } from 'providers/Auth';
import { apiCall } from 'services/api';

import { storage } from 'utils/storage';
import { waitExponentially } from 'utils/async';
import { getTimestamp } from 'utils/date';

interface LogErrorParams {
  error: Error;
  reactInfo?: ErrorInfo;

  tries?: number;
}

const MAX_RETRIES = 10;
const LOG_WINDOW_RANGE_SECONDS = 120;

const LOG_KEY = 'error-log-timestamp';

const payloads = new Set();

function shouldLog() {
  if (env.isLocal) return false;

  const lastLog = storage.durable.get(LOG_KEY);

  if (!lastLog) return true;

  const timePassed = differenceInSeconds(new Date(lastLog), new Date());

  return Math.abs(timePassed) > LOG_WINDOW_RANGE_SECONDS;
}

function setLogged() {
  storage.durable.set(LOG_KEY, new Date().toISOString());
}

async function sendError(payload: any, tries = 0) {
  try {
    await apiCall({
      path: '/infra/web/error',
      method: 'post',
      payload,
    });

    setLogged();
  } catch {
    if (tries > MAX_RETRIES) return;

    await waitExponentially(tries);

    sendError(payload, tries + 1);
  }
}

export function logError({ error, reactInfo }: LogErrorParams) {
  if (!shouldLog()) return;

  const loggedUser = getSavedUserInfo();

  const payload = {
    reactInfo,

    error: {
      message: error.message,
      stack: error.stack,
    },

    userId: loggedUser?.user?.id || 'UNAVAILABLE',

    url: window.location.href,

    timestamp: getTimestamp(),
  };

  const serialized = JSON.stringify(payload);

  if (payloads.has(serialized)) return;

  payloads.add(serialized);

  sendError(payload);
}
